import { useQuery } from "@tanstack/react-query";

import type { InvoiceDocument } from "~/shared/schemas/invoice-schema.ts";
import { invoiceDocumentArraySchema } from "~/shared/schemas/invoice-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

export async function fetchInvoiceDocuments(
	poNumber: number | undefined,
	signal?: AbortSignal,
	isInternal = false,
): Promise<InvoiceDocument[] | null> {
	// Documents can be regenerated behind the same URL (e.g. after "Generate LTL BOL and
	// Labels"), so the browser's HTTP cache must be bypassed on every call, not just React Query's.
	const requestOptions = {
		cache: "no-store" as const,
		...(signal ? { signal } : {}),
	};
	const url = isInternal
		? apiRoutes.GETSHIPMENTDOCUMENTS.replace(
				":poNumber",
				poNumber?.toString() || "",
			)
		: apiRoutes.GETINVOICESDOCUMENTS.replace(
				":poNumber",
				poNumber?.toString() || "",
			);
	try {
		const resp = await getAndValidate(
			url,
			invoiceDocumentArraySchema,
			requestOptions,
		);
		if (resp.success) {
			return resp.data;
		} else {
			console.error("Failed to fetch invoice documents:", resp.error);
			return null;
		}
	} catch (error) {
		console.error("Failed to fetch invoice documents:", error);
		return null;
	}
}

export const useGetInvoiceDocuments = (
	poNumber: number | undefined,
	enabled = true,
	isInternal = false,
) => {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["invoiceDocuments", poNumber],
		queryFn: async ({ signal }) => {
			return await fetchInvoiceDocuments(poNumber, signal, isInternal);
		},
		enabled: !!poNumber && enabled,
		staleTime: 0,
		gcTime: 0,
	});
	return {
		documents: data ?? [],
		isLoading,
		error,
		refetch,
	};
};
